import { Bitmap } from "./bitmap.js";
import { COLORS, type Color } from "./palette.js";
import { FLOOR_Y, SCENE_H, SCENE_W, WALK_Y, skyBands } from "./room.js";

/** Where the scene is. Home is the flat (composeRoom); the rest are short visits away from it. */
export const PLACES = ["home", "cafe", "bar", "park", "office"] as const;
export type Place = (typeof PLACES)[number];
export type AwayPlace = Exclude<Place, "home">;

export interface PlaceView {
  /** Local time in minutes: sky in windows and in the park. */
  minutes: number;
  /** Deterministic per-day seed. */
  seed: number;
}

export interface Spot {
  x: number;
  y: number;
  anim: string;
}

/** Where the companion and the friend are in each place, and what they do there. */
export const PLACE_SPOTS: Record<AwayPlace, { me: Spot; friend: Spot | null }> = {
  cafe: { me: { x: 80, y: 100, anim: "coffee_sit" }, friend: { x: 114, y: 100, anim: "coffee_sit" } },
  bar: { me: { x: 86, y: WALK_Y, anim: "beer_cheers" }, friend: { x: 108, y: WALK_Y, anim: "beer_cheers" } },
  park: { me: { x: 70, y: WALK_Y, anim: "stroll" }, friend: { x: 54, y: WALK_Y, anim: "stroll" } },
  office: { me: { x: 98, y: 100, anim: "desk_work" }, friend: null },
};

/** Park walk: both stroll between these x positions. */
export const PARK_PATH = { from: 34, to: 160 } as const;

function windowWithSky(bm: Bitmap, x: number, y: number, w: number, h: number, v: PlaceView, frame: Color) {
  bm.rect(x - 1, y - 1, w + 2, h + 2, COLORS.ink);
  bm.rect(x, y, w, h, frame);
  const bands = skyBands(v.minutes);
  const ih = h - 4;
  const bandH = Math.ceil(ih / bands.length);
  bands.forEach((c, i) => bm.rect(x + 2, y + 2 + i * bandH, w - 4, Math.min(bandH, ih - i * bandH), c));
  const night = v.minutes < 5 * 60 || v.minutes >= 21 * 60;
  // Street across the road: a few buildings with lit windows at night.
  const base = y + h - 2;
  for (let i = 0, bx = x + 2; bx < x + w - 2; i++) {
    const bw = 6 + ((v.seed + i * 5) % 6);
    const bh = 6 + ((v.seed * 3 + i * 7) % 10);
    const cw = Math.min(bw, x + w - 2 - bx);
    bm.rect(bx, base - bh, cw, bh, night ? COLORS.ink : COLORS.dusk);
    if (night && cw > 3) bm.set(bx + 2, base - bh + 3, COLORS.yellow);
    bx += bw + 1;
  }
}

function hangingLamp(bm: Bitmap, x: number, len: number, shade: Color) {
  bm.rect(x, 0, 1, len, COLORS.ink);
  bm.rect(x - 3, len, 7, 3, shade);
  bm.rect(x - 4, len + 2, 9, 1, COLORS.ink);
  bm.rect(x - 1, len + 3, 3, 1, COLORS.yellowLight);
}

function plant(bm: Bitmap, x: number, y: number) {
  bm.rect(x, y - 6, 6, 6, COLORS.redDark);
  bm.rect(x + 1, y - 7, 4, 1, COLORS.red);
  bm.rect(x - 1, y - 14, 8, 8, COLORS.greenDark);
  bm.rect(x + 1, y - 17, 4, 4, COLORS.green);
  bm.set(x - 2, y - 10, COLORS.green);
  bm.set(x + 7, y - 12, COLORS.green);
}

function plankFloor(bm: Bitmap, base: Color, dark: Color, light: Color) {
  bm.rect(0, FLOOR_Y, SCENE_W, SCENE_H - FLOOR_Y, base);
  for (let y = FLOOR_Y + 5; y < SCENE_H; y += 6) bm.rect(0, y, SCENE_W, 1, dark);
  for (let y = FLOOR_Y, row = 0; y < SCENE_H; y += 6, row++)
    for (let x = (row * 23) % 32; x < SCENE_W; x += 32) bm.rect(x, y, 1, 5, dark);
  bm.rect(0, FLOOR_Y, SCENE_W, 1, light);
}

function cafe(v: PlaceView): Bitmap {
  const bm = new Bitmap(SCENE_W, SCENE_H, 0, 0);
  bm.rect(0, 0, SCENE_W, FLOOR_Y, COLORS.cream);
  bm.rect(0, 0, SCENE_W, 3, COLORS.woodLight);
  // Wainscot.
  bm.rect(0, 64, SCENE_W, FLOOR_Y - 64, COLORS.woodLight);
  for (let x = 4; x < SCENE_W; x += 12) bm.rect(x, 66, 1, FLOOR_Y - 68, COLORS.floor);
  bm.rect(0, 63, SCENE_W, 1, COLORS.floor);
  plankFloor(bm, COLORS.floor, COLORS.floorDark, COLORS.floorLight);
  // Big shop window with the street.
  windowWithSky(bm, 8, 14, 50, 44, v, COLORS.teal);
  bm.rect(32, 14, 2, 44, COLORS.teal);
  bm.rect(8, 10, 50, 3, COLORS.red);
  for (let x = 9; x < 58; x += 4) bm.rect(x, 13, 2, 2, COLORS.paper);
  // Menu board.
  bm.rect(66, 14, 30, 22, COLORS.ink);
  bm.rect(67, 15, 28, 20, COLORS.greyDark);
  bm.rect(70, 18, 14, 1, COLORS.paper);
  bm.rect(70, 22, 18, 1, COLORS.greyLight);
  bm.rect(70, 25, 12, 1, COLORS.greyLight);
  bm.rect(70, 28, 16, 1, COLORS.greyLight);
  bm.rect(88, 27, 3, 3, COLORS.yellow);
  // Counter with an espresso machine and cups.
  bm.rect(132, 60, 60, 34, COLORS.floorDark);
  bm.rect(132, 58, 60, 3, COLORS.woodLight);
  bm.rect(132, 57, 60, 1, COLORS.ink);
  for (let x = 136; x < SCENE_W; x += 10) bm.rect(x, 64, 6, 26, COLORS.floor);
  bm.rect(150, 42, 18, 15, COLORS.grey);
  bm.rect(151, 43, 16, 4, COLORS.greyLight);
  bm.rect(154, 50, 2, 4, COLORS.ink);
  bm.rect(161, 50, 2, 4, COLORS.ink);
  bm.rect(153, 54, 4, 3, COLORS.paper);
  bm.rect(172, 52, 4, 5, COLORS.paper);
  bm.rect(177, 52, 4, 5, COLORS.paper);
  bm.rect(140, 49, 5, 8, COLORS.yellow);
  bm.rect(140, 48, 5, 1, COLORS.ink);
  // Shelf with jars.
  bm.rect(134, 26, 52, 2, COLORS.floor);
  for (let i = 0; i < 6; i++) bm.rect(137 + i * 8, 19, 5, 7, i % 2 ? COLORS.orange : COLORS.brown);
  hangingLamp(bm, 82, 40, COLORS.greenDark);
  hangingLamp(bm, 112, 40, COLORS.greenDark);
  plant(bm, 62, FLOOR_Y);
  // Round table between the two seats.
  bm.rect(88, 84, 20, 3, COLORS.woodLight);
  bm.rect(88, 87, 20, 1, COLORS.floorDark);
  bm.rect(97, 88, 2, 12, COLORS.greyDark);
  bm.rect(92, 100, 12, 2, COLORS.greyDark);
  // Chairs.
  for (const cx of [72, 120]) {
    bm.rect(cx, 72, 3, 26, COLORS.brown);
    bm.rect(cx - 5, 92, 13, 2, COLORS.brown);
  }
  return bm;
}

function bar(v: PlaceView): Bitmap {
  const bm = new Bitmap(SCENE_W, SCENE_H, 0, 0);
  bm.rect(0, 0, SCENE_W, FLOOR_Y, COLORS.brown);
  for (let x = 0; x < SCENE_W; x += 8) bm.rect(x, 0, 1, FLOOR_Y, COLORS.floorDark);
  plankFloor(bm, COLORS.floorDark, COLORS.ink, COLORS.floor);
  // Back shelves with bottles.
  const bottle = [COLORS.green, COLORS.orange, COLORS.redDark, COLORS.teal, COLORS.yellow, COLORS.greenDark];
  for (const sy of [22, 40]) {
    bm.rect(22, sy, 148, 2, COLORS.woodLight);
    for (let i = 0; i < 18; i++) {
      const bx = 26 + i * 8;
      const c = bottle[(i + sy + v.seed) % bottle.length]!;
      bm.rect(bx, sy - 9, 4, 9, c);
      bm.rect(bx + 1, sy - 12, 2, 3, c);
      bm.set(bx + 1, sy - 7, COLORS.paper);
    }
  }
  // Neon sign.
  bm.rect(70, 48, 52, 12, COLORS.ink);
  bm.rect(72, 50, 48, 1, COLORS.pink);
  bm.rect(72, 57, 48, 1, COLORS.pink);
  for (let i = 0; i < 4; i++) bm.rect(78 + i * 10, 52, 6, 4, i % 2 ? COLORS.yellow : COLORS.pink);
  // Taps and counter.
  bm.rect(20, 66, 152, 28, COLORS.floor);
  bm.rect(18, 63, 156, 4, COLORS.woodLight);
  bm.rect(18, 62, 156, 1, COLORS.ink);
  for (let x = 26; x < 170; x += 16) bm.rect(x, 70, 10, 20, COLORS.floorDark);
  for (const tx of [40, 46, 52]) {
    bm.rect(tx, 54, 2, 9, COLORS.greyLight);
    bm.rect(tx - 1, 53, 4, 2, COLORS.yellow);
  }
  bm.rect(18, 91, 156, 3, COLORS.yellow);
  // Stools.
  for (const sx of [60, 132, 152]) {
    bm.rect(sx, 92, 12, 3, COLORS.redDark);
    bm.rect(sx + 5, 95, 2, 12, COLORS.greyDark);
    bm.rect(sx + 2, 106, 8, 1, COLORS.greyDark);
  }
  hangingLamp(bm, 60, 14, COLORS.yellow);
  hangingLamp(bm, 130, 14, COLORS.yellow);
  return bm;
}

function tree(bm: Bitmap, x: number, y: number, big: boolean) {
  const r = big ? 14 : 9;
  bm.rect(x - 2, y - r - 10, 4, r + 10, COLORS.brown);
  bm.rect(x - r, y - r * 2 - 8, r * 2, r + 2, COLORS.greenDark);
  bm.rect(x - r + 3, y - r * 2 - 12, r * 2 - 6, 4, COLORS.greenDark);
  bm.rect(x - r + 4, y - r * 2 - 6, r - 2, 4, COLORS.green);
  bm.rect(x + 2, y - r * 2 + 1, r - 4, 3, COLORS.green);
}

function park(v: PlaceView): Bitmap {
  const bm = new Bitmap(SCENE_W, SCENE_H, 0, 0);
  const bands = skyBands(v.minutes);
  const bandH = Math.ceil(70 / bands.length);
  bands.forEach((c, i) => bm.rect(0, i * bandH, SCENE_W, bandH, c));
  const night = v.minutes < 5 * 60 || v.minutes >= 21 * 60;
  if (night)
    for (let i = 0; i < 18; i++)
      bm.set((v.seed * 13 + i * 31) % SCENE_W, (v.seed * 7 + i * 13) % 40, COLORS.paper);
  else {
    const t = Math.max(0, Math.min(1, (v.minutes / 60 - 6) / 14));
    bm.rect(10 + Math.round(t * 170), 8 + Math.round(Math.abs(t - 0.5) * 30), 6, 6, COLORS.yellow);
  }
  // Distant hills and trees.
  bm.rect(0, 62, SCENE_W, 8, COLORS.greenDark);
  for (let x = 0; x < SCENE_W; x += 14) bm.rect(x, 58 - ((x * 7 + v.seed) % 5), 10, 6, COLORS.greenDark);
  // Grass and path.
  bm.rect(0, 70, SCENE_W, SCENE_H - 70, COLORS.green);
  for (let i = 0; i < 40; i++) bm.set((i * 47 + v.seed) % SCENE_W, 72 + ((i * 19) % 22), COLORS.greenDark);
  bm.rect(0, 98, SCENE_W, 14, COLORS.cream);
  bm.rect(0, 98, SCENE_W, 1, COLORS.woodLight);
  bm.rect(0, 111, SCENE_W, 1, COLORS.woodLight);
  for (let x = 6; x < SCENE_W; x += 18) bm.rect(x, 104, 3, 1, COLORS.woodLight);
  // Trees, bench, lamp post, pond.
  tree(bm, 22, 92, true);
  tree(bm, 168, 92, true);
  tree(bm, 120, 84, false);
  bm.rect(60, 86, 26, 2, COLORS.brown);
  bm.rect(60, 81, 26, 2, COLORS.brown);
  bm.rect(62, 88, 2, 8, COLORS.ink);
  bm.rect(82, 88, 2, 8, COLORS.ink);
  bm.rect(146, 56, 2, 40, COLORS.greyDark);
  bm.rect(143, 52, 8, 5, COLORS.ink);
  bm.rect(144, 53, 6, 3, night ? COLORS.yellowLight : COLORS.greyLight);
  bm.rect(96, 76, 22, 6, COLORS.skyHigh);
  bm.rect(98, 75, 18, 1, COLORS.skyDay);
  bm.rect(98, 82, 18, 1, COLORS.greenDark);
  return bm;
}

function office(v: PlaceView): Bitmap {
  const bm = new Bitmap(SCENE_W, SCENE_H, 0, 0);
  bm.rect(0, 0, SCENE_W, FLOOR_Y, COLORS.greyLight);
  bm.rect(0, 0, SCENE_W, 3, COLORS.grey);
  bm.rect(0, FLOOR_Y - 3, SCENE_W, 3, COLORS.grey);
  // Carpet.
  bm.rect(0, FLOOR_Y, SCENE_W, SCENE_H - FLOOR_Y, COLORS.blueDark);
  for (let i = 0; i < 30; i++)
    bm.set((i * 53 + v.seed) % SCENE_W, FLOOR_Y + 2 + ((i * 11) % 22), COLORS.blue);
  bm.rect(0, FLOOR_Y, SCENE_W, 1, COLORS.grey);
  // Two windows with the skyline.
  windowWithSky(bm, 10, 12, 44, 40, v, COLORS.greyDark);
  windowWithSky(bm, 138, 12, 44, 40, v, COLORS.greyDark);
  // Whiteboard with notes.
  bm.rect(64, 14, 46, 28, COLORS.greyDark);
  bm.rect(65, 15, 44, 26, COLORS.paper);
  bm.rect(68, 19, 20, 1, COLORS.blue);
  bm.rect(68, 23, 30, 1, COLORS.greyDark);
  bm.rect(68, 27, 24, 1, COLORS.greyDark);
  bm.rect(96, 30, 8, 7, COLORS.yellow);
  bm.rect(84, 32, 8, 6, COLORS.pink);
  // Clock.
  bm.rect(118, 16, 10, 10, COLORS.ink);
  bm.rect(119, 17, 8, 8, COLORS.paper);
  const hand = Math.floor(v.minutes / 60) % 12;
  bm.rect(122, 18 + (hand < 6 ? 0 : 2), 1, 3, COLORS.ink);
  bm.rect(122, 21, 3, 1, COLORS.ink);
  // Neighbouring desks in the back.
  for (const dx of [8, 150]) {
    bm.rect(dx, 70, 34, 3, COLORS.grey);
    bm.rect(dx + 2, 73, 2, 21, COLORS.greyDark);
    bm.rect(dx + 30, 73, 2, 21, COLORS.greyDark);
    bm.rect(dx + 10, 58, 14, 10, COLORS.ink);
    bm.rect(dx + 11, 59, 12, 8, COLORS.teal);
    bm.rect(dx + 16, 68, 2, 2, COLORS.greyDark);
  }
  plant(bm, 58, FLOOR_Y);
  plant(bm, 132, FLOOR_Y);
  return bm;
}

/** Background of an away place (composed once per minute bucket by the renderer). */
export function composePlace(place: AwayPlace, v: PlaceView): Bitmap {
  switch (place) {
    case "cafe":
      return cafe(v);
    case "bar":
      return bar(v);
    case "park":
      return park(v);
    case "office":
      return office(v);
  }
}

/** Things drawn in front of the characters (the companion's desk hides his legs at work). */
export function composePlaceFront(place: AwayPlace): Bitmap | null {
  if (place !== "office") return null;
  const bm = new Bitmap(SCENE_W, SCENE_H, 0, 0);
  bm.rect(74, 88, 48, 4, COLORS.grey);
  bm.rect(74, 87, 48, 1, COLORS.ink);
  bm.rect(76, 92, 3, 18, COLORS.greyDark);
  bm.rect(117, 92, 3, 18, COLORS.greyDark);
  bm.rect(80, 92, 36, 10, COLORS.greyDark);
  // Monitor seen from behind the desk, mug and a plant.
  bm.rect(104, 70, 16, 12, COLORS.ink);
  bm.rect(105, 71, 14, 10, COLORS.teal);
  bm.rect(110, 82, 4, 5, COLORS.greyDark);
  bm.rect(80, 83, 4, 4, COLORS.paper);
  return bm;
}

/** Deterministic look for the friend met in a place (seeded so a visit keeps one friend). */
export function friendLook(seed: number): {
  skin: number;
  hair: number;
  hairColor: number;
  beard: number;
  glasses: boolean;
  shirt: number;
} {
  const r = (n: number, k: number) => Math.abs((seed * 2654435761 + k * 40503) >>> 0) % n;
  return {
    skin: r(5, 1),
    hair: r(6, 2),
    hairColor: r(8, 3),
    beard: r(4, 4),
    glasses: r(3, 5) === 0,
    shirt: r(8, 6),
  };
}
